import { TmcRepository } from '../repositories/tmc.repository'
import {
    AcceptAdmissionPayload,
    AcceptRelocationPayload,
    DownloadRemainsPayloadT,
    DownloadRemainsResponseT,
    DownloadTmcPayloadT,
    DownloadTmcResponseT,
    OrdersDownloadAdmissionsPayloadT,
    OrdersDownloadAdmissionsResponseT,
    OrdersDownloadRelocationsPayloadT,
    OrdersDownloadRelocationsResponseT,
    OrdersDownloadWriteoffsPayloadT,
    OrdersDownloadWriteoffsResponseT,
    RemainsGetListParamsT,
    TmcCreateAdmissionPayload,
    TmcCreatePayload,
    TmcCreateRelocationPayload,
    TmcGetAdmissionResponseT,
    TmcGetAdmissionsListParams,
    TmcGetAdmissionsListResponseT,
    TmcGetListParams,
    TmcGetListResponseT,
    TmcGetRelocationListResponseT,
    TmcGetRelocationResponseT,
    TmcGetRelocationsListParams,
    TmcGetRemainsListResponseT,
    TmcGetUnitsListResponseT,
    TmcGetWriteoffResponseT,
    TmcGetWriteoffsResponseT,
    TmcSecondaryMaterialPayload,
    WriteOffsGetListParams,
    WriteoffPayloadI,
    WriteoffUpdatePayloadI,
} from '../types/tmc.types'

const tmcRepository = new TmcRepository()

export class TmcService {
    async getList(
        page: number,
        search?: string | null,
        params?: TmcGetListParams
    ): Promise<TmcGetListResponseT> {
        const response = await tmcRepository.getList(page, search, params)
        return response.data
    }

    async createTMC(payload: TmcCreatePayload): Promise<void> {
        const response = await tmcRepository.createTMC(payload)
        return response.data
    }

    async getAdmissionsList(
        params: TmcGetAdmissionsListParams
    ): Promise<TmcGetAdmissionsListResponseT> {
        const response = await tmcRepository.getAdmissionsList(params)
        return response.data
    }

    async getAdmission(admission_id: number): Promise<TmcGetAdmissionResponseT> {
        const response = await tmcRepository.getAdmission(admission_id)
        return response.data
    }

    async createAdmission(payload: TmcCreateAdmissionPayload): Promise<void> {
        const response = await tmcRepository.createAdmission(payload)
        return response.data
    }

    async createSecondaryMaterial(payload: TmcSecondaryMaterialPayload): Promise<void> {
        const response = await tmcRepository.createSecondaryMaterial(payload)
        return response.data
    }

    async updateAdmission(admission_id: number, payload: TmcCreateAdmissionPayload): Promise<void> {
        const response = await tmcRepository.updateAdmission(admission_id, payload)
        return response.data
    }

    async updateAdmissionSecondary(
        admission_id: number,
        payload: TmcSecondaryMaterialPayload
    ): Promise<void> {
        const response = await tmcRepository.updateAdmissionSecondary(admission_id, payload)
        return response.data
    }

    async completeAdmission(receipt_id: number, payload: AcceptAdmissionPayload): Promise<void> {
        const response = await tmcRepository.completeAdmission(receipt_id, payload)
        return response.data
    }

    async cancelAdmission(receipt_id: number): Promise<void> {
        const response = await tmcRepository.cancelAdmission(receipt_id)
        return response.data
    }

    async getRelocationList(
        params: TmcGetRelocationsListParams
    ): Promise<TmcGetRelocationListResponseT> {
        const response = await tmcRepository.getRelocationList(params)
        return response.data
    }

    async getRelocation(relocation_id: number): Promise<TmcGetRelocationResponseT> {
        const response = await tmcRepository.getRelocation(relocation_id)
        return response.data
    }

    async createRelocation(payload: TmcCreateRelocationPayload): Promise<void> {
        const response = await tmcRepository.createRelocation(payload)
        return response.data
    }

    async updateRelocation(
        relocation_id: number,
        payload: TmcCreateRelocationPayload
    ): Promise<void> {
        const response = await tmcRepository.updateRelocation(relocation_id, payload)
        return response.data
    }

    async completeRelocation(
        relocation_id: number,
        payload: AcceptRelocationPayload
    ): Promise<void> {
        const response = await tmcRepository.completeRelocation(relocation_id, payload)
        return response.data
    }

    async cancelRelocation(relocation_id: number): Promise<void> {
        const response = await tmcRepository.cancelRelocation(relocation_id)
        return response.data
    }

    async cancelWriteOff(id: number): Promise<void> {
        const response = await tmcRepository.cancelWriteOff(id)
        return response.data
    }

    async getUnitsList(page: number, search?: string): Promise<TmcGetUnitsListResponseT> {
        const response = await tmcRepository.getUnitsList(page, search)
        return response.data
    }

    async getRemainsList(params: RemainsGetListParamsT): Promise<TmcGetRemainsListResponseT> {
        const response = await tmcRepository.getRemainsList(params)
        return response.data
    }

    async writeOff(payload: FormData): Promise<void> {
        const response = await tmcRepository.writeOff(payload)
        return response.data
    }

    async updateWriteOff(writeoff_id: number, payload: FormData): Promise<void> {
        const response = await tmcRepository.updateWriteOff(writeoff_id, payload)
        return response.data
    }

    async getWriteOffs(params: WriteOffsGetListParams): Promise<TmcGetWriteoffsResponseT> {
        const response = await tmcRepository.getWriteOffs(params)
        return response.data
    }

    async getWriteOff(writeoff_id: number): Promise<TmcGetWriteoffResponseT> {
        const response = await tmcRepository.getWriteOff(writeoff_id)
        return response.data
    }

    async deleteReceiptItem(receipt_item_id: number): Promise<void> {
        const response = await tmcRepository.deleteReceiptItem(receipt_item_id)
        return response.data
    }

    async deletePackageFile(id: number): Promise<void> {
        const response = await tmcRepository.deletePackageFile(id)
        return response.data
    }

    async deleteDirectionItem(direction_item_id: number): Promise<void> {
        const response = await tmcRepository.deleteDirectionItem(direction_item_id)
        return response.data
    }

    async deleteWriteoffItem(writeoff_item_id: number): Promise<void> {
        const response = await tmcRepository.deleteWriteoffItem(writeoff_item_id)
        return response.data
    }

    async exportExcelAdmissions(
        params: OrdersDownloadAdmissionsPayloadT
    ): Promise<OrdersDownloadAdmissionsResponseT> {
        const response = await tmcRepository.exportExcelAdmissions(params)
        return response.data
    }

    async exportExcelRemains(params: DownloadRemainsPayloadT): Promise<DownloadRemainsResponseT> {
        const response = await tmcRepository.exportExcelRemains(params)
        return response.data
    }

    async exportExcelTmc(params: DownloadTmcPayloadT): Promise<DownloadTmcResponseT> {
        const response = await tmcRepository.exportExcelTmc(params)
        return response.data
    }

    async exportExcelRelocations(
        params: OrdersDownloadRelocationsPayloadT
    ): Promise<OrdersDownloadRelocationsResponseT> {
        const response = await tmcRepository.exportExcelRelocations(params)
        return response.data
    }

    async exportExcelWriteoffs(
        params: OrdersDownloadWriteoffsPayloadT
    ): Promise<OrdersDownloadWriteoffsResponseT> {
        const response = await tmcRepository.exportExcelWriteoffs(params)
        return response.data
    }

    async importInvoices(form_data: FormData): Promise<void> {
        const response = await tmcRepository.importInvoices(form_data)
        return response.data
    }
}
